
import React, { useState, useEffect, useMemo } from 'react';
import { MoreHorizontal, Calendar, User, Mail, Ban, CheckCircle2, Loader2, MessageSquare, Plus, Upload, X, Search, UserPlus, BrainCircuit, Star, Send, MoveRight, Phone, MessageCircle, Sparkles, BarChart2, Settings, Building2, Clock, FileText, ChevronDown, ThumbsUp, ThumbsDown, ArrowRight, MapPin, Tag, AlertTriangle, MonitorSmartphone } from 'lucide-react';
import { supabase_get_vacancy_applications, supabase_fetch_kanban_applications_for_vacancies, supabase, supabase_get_talent_pool, supabase_get_candidates_light, supabase_submit_application, supabase_invite_vacancy_reviewer, supabase_save_candidate_review, supabase_get_candidate_reviews, supabase_update_application_status, supabase_get_candidate_profile_by_id, supabase_get_templates, supabase_log_interaction, supabase_recruiter_publish_portal_proposal, supabase_recruiter_apply_voorgesteld_match_score } from '../services/supabaseService';
import { analyzeUploadedCV, performRealMatch, matchTalentPool } from '../services/geminiService';
import { KanbanColumn, CandidateReview, AutomationTemplate, UserRole, VacancyStage, BULLHORN_STATUS_MAP } from '../types';
import CandidateDetailModal from './CandidateDetailModal';
import VacancyAddCandidateModal from './VacancyAddCandidateModal';
import TeamsMeetingScheduler from './TeamsMeetingScheduler';
import FeedbackScorecard from './FeedbackScorecard';
import { triggerAutomation } from '../services/automationService';
import AiRichTextEditor from './AiRichTextEditor';
import CandidateMoveModal from './CandidateMoveModal';
import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';
import BulkEmailComposerModal from './BulkEmailComposerModal';
// Removed MOCK_CANDIDATES import

// Define standard recruitment stages in Dutch
import { DEFAULT_KANBAN_STAGES } from '../constants';
import { getVacancyLifecycleStatus } from './VacancyList';
import {
    isPortalHideCandidateNameForStatus,
    isPortalBlockCandidateDetailForStatus,
    portalDisplayCandidateName,
    portalExcludeApplicationFromPipeline,
    portalPrivacyFromUser,
    isPortalArchivedVacancy,
    sanitizeVacancyIdsForInFilter,
} from '../utils/clientPortalGuards';
import type { VoorgesteldClientPayload } from '../utils/portalProposalThread';
import { getCandidateEmail } from '../utils/candidateContact';
import { VOORGESTELD_RESUME_BUNDLE_KEY, RESUME_VOORGESTELD_MOVE_MODAL_EVENT, resumeBundleMatchesVacancyEvent } from '../utils/voorgesteldResumeNavigation';
import { dbStatusOverridesVoorgesteldKanbanBucket, getLastPortalClientFeedback, isEffectiveVoorgesteldStage } from '../utils/vacancyClientPortalStage';
import { foldSearchText, searchTextIncludes } from '../utils/searchText';
import { formatPipelineInterviewHint, getClientPortalConsentBadgeLabel, getClientPortalDeclineBadgeLabel } from '../utils/clientPipelineConsent';
import {
    applicationMatchesClientFilters,
    vacancyMatchesLifecycleFilter,
    vacancyMatchesLifecycleStages,
    type VacancyLifecycleStage,
} from '../utils/vacancyClientFilters';

interface KanbanBoardProps {
    vacancyId?: string;
    /** Wanneer gezet: filter ALL_*-queries op deze stages i.p.v. alleen `vacancyId`. */
    lifecycleStages?: VacancyLifecycleStage[];
    selectedVacancyId?: string;
    userRole?: UserRole;
    isReviewerOnly?: boolean;
    onAnalytics?: () => void;
    onConfigFlow?: () => void;
    onCandidateSelect?: (candidateId: string) => void;
    onCustomAddCandidateClick?: () => void;
    globalVacancies?: any[];
    filterOpdrachtgever?: string;
    filterEindklant?: string;
    filterStatus?: string | string[];
    searchQuery?: string;
    user?: any;
}

function candidateCvSnippetForVoorgesteldAi(raw: unknown): string | undefined {
    const s = String(raw ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return s.length ? s.slice(0, 8000) : undefined;
}

type KanbanMoveDetailsPayload = {
    candidateId: string;
    candidateRealId: string;
    fromStage: string;
    toStage: string;
    candidateName: string;
    candidateEmail?: string;
    vacancyTitle?: string;
    clientName?: string;
    defaultClientEmail?: string;
    applicationId?: string;
    vacancyId?: string;
    clientId?: string | null;
    matchScore?: number;
    candidateCvSnippet?: string;
};

function buildKanbanMoveDetails(
    candidate: {
        id: string;
        candidateId: string;
        name: string;
        email?: string;
        vacancyTitle?: string;
        companyName?: string;
        defaultClientEmail?: string;
        matchScore?: number;
        cvSummary?: string;
    },
    fromStage: string,
    toStage: string,
    ctx: { vacancyId: string; clientId: string | null },
): KanbanMoveDetailsPayload {
    return {
        candidateId: candidate.id,
        candidateRealId: candidate.candidateId,
        candidateName: candidate.name,
        candidateEmail: candidate.email || '',
        fromStage,
        toStage,
        vacancyTitle: candidate.vacancyTitle,
        clientName: candidate.companyName,
        defaultClientEmail: candidate.defaultClientEmail,
        applicationId: candidate.id,
        vacancyId: ctx.vacancyId || '',
        clientId: ctx.clientId,
        matchScore: candidate.matchScore,
        candidateCvSnippet: candidateCvSnippetForVoorgesteldAi(candidate.cvSummary),
    };
}


const KanbanBoard: React.FC<KanbanBoardProps> = ({
    vacancyId,
    lifecycleStages,
    selectedVacancyId,
    userRole,
    isReviewerOnly = false,
    onAnalytics,
    onConfigFlow,
    onCandidateSelect,
    onCustomAddCandidateClick,
    globalVacancies,
    filterOpdrachtgever,
    filterEindklant,
    filterStatus,
    searchQuery,
    user,
}) => {
    const [stages, setStages] = useState<VacancyStage[]>(DEFAULT_KANBAN_STAGES);
    const [columns, setColumns] = useState<KanbanColumn[]>([]);
    const [activeStage, setActiveStage] = useState('New');
    const [filterType, setFilterType] = useState<'Qualified' | 'Disqualified'>('Qualified');
    const [isLoading, setIsLoading] = useState(false);
    const [vacancyTitle, setVacancyTitle] = useState('');
    /** Gecachte ruwe sollicitaties — filters/zoeken herberekenen kolommen zonder opnieuw te fetchen. */
    const kanbanRawAppsRef = React.useRef<any[]>([]);
    const [kanbanAppsLoaded, setKanbanAppsLoaded] = useState(0);

    // Add Candidate Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [selectedBulkCandidateIds, setSelectedBulkCandidateIds] = useState<string[]>([]);
    const [showBulkWhatsApp, setShowBulkWhatsApp] = useState(false);
    const [showBulkEmail, setShowBulkEmail] = useState(false);

    const getSelectedCandidatesData = () => {
        const all: any[] = [];
        columns.forEach(c => c.candidates.forEach(cand => {
           if (selectedBulkCandidateIds.includes(cand.id)) {
              all.push({
                 id: cand.candidateId,
                 name: cand.name,
                 email: cand.email || '',
                 phone: cand.phone || '',
                 vacancyTitle: cand.vacancyTitle || '',
                 companyName: cand.companyName || '',
                 profile_id: cand.candidateId
              });
           }
        }));
        return all;
    };

    const toggleBulkSelect = (id: string) => {
        if (selectedBulkCandidateIds.includes(id)) setSelectedBulkCandidateIds(prev => prev.filter(i => i !== id));
        else setSelectedBulkCandidateIds(prev => [...prev, id]);
    };

    const [addMode, setAddMode] = useState<'new' | 'pool'>('new');
    const [clientId, setClientId] = useState<string | null>(null);

    // Invite Reviewer State
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [isInviting, setIsInviting] = useState(false);

    // Review Candidate State
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewCandidate, setReviewCandidate] = useState<any>(null);
    const [reviewScore, setReviewScore] = useState(0);
    const [reviewComment, setReviewComment] = useState('');
    const [isSavingReview, setIsSavingReview] = useState(false);

    // AI Match Modal State
    const [showAiModal, setShowAiModal] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState<any>(null);
    const [isAiMatching, setIsAiMatching] = useState(false);
    const [aiMatchCandidateId, setAiMatchCandidateId] = useState<string | null>(null);

    // Old quick action state removed inline inline MoveModal used instead

    // Upload/Parse State
    // Upload/Parse State
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [parsedCandidate, setParsedCandidate] = useState<any>(null);
    const [isSavingNew, setIsSavingNew] = useState(false);
    const [candidateSource, setCandidateSource] = useState<string>('Handmatig');

    // Pool Selection State
    const [poolCandidates, setPoolCandidates] = useState<any[]>([]);
    const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
    const [poolSearch, setPoolSearch] = useState('');
    const [isPoolLoading, setIsPoolLoading] = useState(false);
    
    // Add Candidate Target Config
    const [targetAddVacancyId, setTargetAddVacancyId] = useState<string>('');
    const [targetAddStageId, setTargetAddStageId] = useState<string>('');
    const [availableVacancies, setAvailableVacancies] = useState<any[]>([]);
    const [vacancySearchQuery, setVacancySearchQuery] = useState('');
    const [targetVacancyDropdownOpen, setTargetVacancyDropdownOpen] = useState(false);
    const [showClosedVacancies, setShowClosedVacancies] = useState(false);

    // AI Pool Search State
    const [poolSearchQuery, setPoolSearchQuery] = useState('');
    const [poolLocationFilter, setPoolLocationFilter] = useState('');
    const [poolStatusFilter, setPoolStatusFilter] = useState('');
    const [isPoolSearching, setIsPoolSearching] = useState(false);
    
    const [aiMatchedPoolIds, setAiMatchedPoolIds] = useState<string[]>([]);
    const [aiMatchReasoning, setAiMatchReasoning] = useState<string>('');

    // Drag & Drop + Move Modal State
    const [draggedCandidateId, setDraggedCandidateId] = useState<string | null>(null);
    const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveDetails, setMoveDetails] = useState<KanbanMoveDetailsPayload | null>(null);
    const [isMoving, setIsMoving] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [deleteAppId, setDeleteAppId] = useState<string | null>(null);
    const [isUpdatingMeta, setIsUpdatingMeta] = useState<Record<string, boolean>>({});

    // Column Sorting and Searching
    const [columnSort, setColumnSort] = useState<Record<string, 'default' | 'az' | 'date'>>({});
    const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

    // Templates State
    const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

    // Candidate Detail Modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);

    // Interview Scheduler State
    const [showScheduler, setShowScheduler] = useState(false);
    const [scheduleCandidate, setScheduleCandidate] = useState<{
        applicationId: string;
        candidateId: string;
        name: string;
        stageId?: string;
        vacancyId?: string;
        initialDate?: string;
    } | null>(null);

    // Feedback State
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackSession, setFeedbackSession] = useState<{ interviewId: string, stageId: string, candidateName: string } | null>(null);

    // Track Opened Candidates
    const [openedCandidateIds, setOpenedCandidateIds] = useState<Set<string>>(new Set());
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [clientEmailTemplates, setClientEmailTemplates] = useState<any[]>([]);
    const [clientEmailSignature, setClientEmailSignature] = useState<string | null>(null);
    const [rejectionReasonsList, setRejectionReasonsList] = useState<string[]>(['Kandidaat teruggetrokken', 'Afgewezen door opdrachtgever', 'Opdracht ingetrokken', 'Overig']);

    const isClientPortal =
        typeof window !== 'undefined' &&
        (window.location.hostname.toLowerCase().includes('clientportal') ||
            new URLSearchParams(window.location.search).get('clientportal') === 'true');

    const portalPrivacy = useMemo(() => portalPrivacyFromUser(user), [user?.portalFullPipelineVisibility]);

    useEffect(() => {
        const fn = (ev: Event) => {
            const d = (ev as CustomEvent<{ vacancyId?: string; applicationId?: string }>).detail;
            if (!d?.vacancyId || !vacancyId || d.vacancyId !== vacancyId) return;
            try {
                const raw = sessionStorage.getItem(VOORGESTELD_RESUME_BUNDLE_KEY);
                if (!raw) return;
                const b = JSON.parse(raw);
                if (!resumeBundleMatchesVacancyEvent(b, d)) return;
                if (!b.move) return;
                setMoveDetails(b.move);
                setShowMoveModal(true);
            } catch {
                /* ignore */
            }
        };
        window.addEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
        return () => window.removeEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
    }, [vacancyId]);

    useEffect(() => {
        const stored = localStorage.getItem('werftools_opened_candidates');
        if (stored) {
            try { setOpenedCandidateIds(new Set(JSON.parse(stored))); } catch(e) {}
        }
        
        const fetchUser = async () => {
             const { data } = await supabase.auth.getUser();
             if (data?.user) {
                 const { data: profile } = await supabase.from('profiles').select('name, client_id').eq('id', data.user.id).single();
                 setCurrentUser({ ...data.user, profile });
                 
                 if (profile?.client_id) {
                     const { data: clientData } = await supabase.from('clients').select('data').eq('id', profile.client_id).single();
                     if (clientData?.data?.email_templates) {
                         setClientEmailTemplates(clientData.data.email_templates);
                     }
                     if (clientData?.data?.user_signatures && clientData.data.user_signatures[data.user.id]) {
                         setClientEmailSignature(clientData.data.user_signatures[data.user.id]);
                     } else if (clientData?.data?.email_signature) {
                         setClientEmailSignature(clientData.data.email_signature);
                     }
                     if (clientData?.data?.ai_matching_settings?.rejectionReasons) {
                         setRejectionReasonsList(clientData.data.ai_matching_settings.rejectionReasons);
                     }
                 }
             }
        };
        fetchUser();
    }, []);

    const markAsOpened = (candidateId: string) => {
        setOpenedCandidateIds(prev => {
            const next = new Set(prev);
            next.add(candidateId);
            localStorage.setItem('werftools_opened_candidates', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const updateApplicationPipelineMeta = async (applicationId: string, patch: Record<string, any>) => {
        if (!applicationId) return;
        if (isClientPortal) return;
        setIsUpdatingMeta((p) => ({ ...p, [applicationId]: true }));
        try {
            // Minimal fetch of current snapshot to merge safely
            const { data: row, error } = await supabase
                .from('applications')
                .select('cv_snapshot')
                .eq('id', applicationId)
                .single();
            if (error) throw error;
            const snap = (row?.cv_snapshot && typeof row.cv_snapshot === 'object') ? row.cv_snapshot : {};
            const next = {
                ...(snap || {}),
                pipeline_meta: {
                    ...((snap as any)?.pipeline_meta || {}),
                    ...patch,
                },
            };
            const { error: upErr } = await supabase
                .from('applications')
                .update({ cv_snapshot: next, updated_at: new Date().toISOString() })
                .eq('id', applicationId);
            if (upErr) throw upErr;
            // Refresh view quietly
            if (vacancyId) {
                const currentStages = stages;
                await loadApplications(vacancyId, currentStages, true);
            }
        } catch (e) {
            console.error('[KanbanBoard] updateApplicationPipelineMeta failed', e);
        } finally {
            setIsUpdatingMeta((p) => ({ ...p, [applicationId]: false }));
        }
    };

    const vacancySnippetForMoveModal = useMemo(() => {
        const vid = vacancyId;
        if (!vid || vid === 'ALL' || String(vid).startsWith('ALL_')) return '';
        const v = globalVacancies?.find((x: any) => x.id === vid);
        const desc = v?.description ?? v?.data?.description ?? '';
        const raw = typeof desc === 'string' ? desc : '';
        return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 2000);
    }, [vacancyId, globalVacancies]);

    const bulkComposerVacancyData = useMemo(
        () => ({ id: vacancyId, title: 'Geselecteerde Vacature' }),
        [vacancyId],
    );

    const kanbanDataFetchKey = useMemo(
        () =>
            [
                vacancyId ?? '',
                lifecycleStages?.join('|') ?? '',
                selectedVacancyId ?? '',
                globalVacancies?.length ?? 0,
            ].join('::'),
        [vacancyId, lifecycleStages, selectedVacancyId, globalVacancies?.length],
    );

    const fetchVacancyDetails = async () => {
        if (!vacancyId || vacancyId.startsWith('0000') || vacancyId === 'ALL' || vacancyId.startsWith('ALL_')) {
            const getTitle = () => {
                if (vacancyId === 'ALL') return 'Alle Vacatures';
                if (vacancyId?.startsWith('ALL_')) {
                    const map: Record<string, string> = {
                        ALL_OPEN: 'Openstaande Vacatures',
                        ALL_GESLOTEN: 'Gesloten Vacatures',
                        ALL_OPEN_GESLOTEN: 'Open + Gesloten',
                        ALL_OPEN_GESLOTEN_DOING: 'Open + Gesloten + Doing',
                        ALL_DOING: 'Doing Vacatures',
                        ALL_OPEN_DOING: 'Open + Doing',
                        ALL_PUBLISHED: 'Gepubliceerde Vacatures',
                    };
                    return `Pipeline - ${map[vacancyId as string] || 'Vacatures'}`;
                }
                return 'Demo Vacature';
            };
            setVacancyTitle(getTitle());
            setClientId('00000000-0000-0000-0000-000000000001');
            return;
        }
        const { data } = await supabase.from('vacancies').select('client_id, title').eq('id', vacancyId).single();
        if (data) {
            setClientId(data.client_id);
            setVacancyTitle(data.title);
        }
    };

    const fetchTemplates = async () => {
        const t = await supabase_get_templates();
        setTemplates(t || []);
    };

    const fetchStages = async (id: string) => {
        // Bypass database to enforce strict user-requested pipeline stages universally
        const mapped: VacancyStage[] = DEFAULT_KANBAN_STAGES.map(s => ({
            id: s.id,
            vacancyId: id,
            name: s.name,
            type: s.type,
            sortOrder: s.sortOrder
        }));
        setStages(mapped);
        return mapped;
    };

    const attachGlobalVacancyEmbeds = (apps: any[]) => {
        const vMap = new Map<string, any>();
        (globalVacancies || []).forEach((v: any) => vMap.set(v.id, v));
        return apps.map((a: any) => {
            const vac = vMap.get(a.vacancy_id);
            if (!vac) return a;
            return {
                ...a,
                vacancies: {
                    title: vac.title,
                    status: vac.status,
                    vacancy_number: vac.vacancy_number,
                    data: vac.data,
                    client_id: vac.client_id,
                    opdrachtgever_id: vac.opdrachtgever_id,
                    eindklant_id: vac.eindklant_id,
                    clients: vac.clients,
                    opdrachtgever: vac.opdrachtgever,
                    eindklant: vac.eindklant,
                },
            };
        });
    };

    const resolveGlobalVacancyIdsForFetch = async (id: string): Promise<string[]> => {
        let filterVacs: any[] = globalVacancies || [];
        if (!filterVacs.length) {
            let vacQuery = supabase
                .from('vacancies')
                .select('id, status, data')
                .order('created_at', { ascending: false })
                .limit(3000);
            if (clientId && !clientId.startsWith('0000')) {
                vacQuery = vacQuery.eq('client_id', clientId);
            }
            const { data } = await vacQuery;
            filterVacs = data || [];
        }

        const lifecycleScoped = filterVacs.filter((v: any) => {
            if (selectedVacancyId) return v.id === selectedVacancyId;
            if (lifecycleStages?.length) {
                return vacancyMatchesLifecycleStages(v, lifecycleStages, {
                    excludePortalArchived: isClientPortal,
                });
            }
            if (id.startsWith('ALL_')) return vacancyMatchesLifecycleFilter(v, id);
            return true;
        });
        return sanitizeVacancyIdsForInFilter(lifecycleScoped.map((v: any) => v.id));
    };

    const fetchKanbanApplicationsRaw = async (id: string): Promise<any[]> => {
        if (id.startsWith('00000000-0000') && id !== 'ALL') return [];

        if (id === 'ALL' || id.startsWith('ALL_')) {
            const vacancyFilter = await resolveGlobalVacancyIdsForFetch(id);
            if (!vacancyFilter.length) return [];
            const apps = await supabase_fetch_kanban_applications_for_vacancies(vacancyFilter);
            return attachGlobalVacancyEmbeds(apps);
        }

        return (await supabase_get_vacancy_applications(id)) || [];
    };

    const applyKanbanProjection = (
        apps: any[],
        id: string,
        currentStages: VacancyStage[],
    ) => {
        const activeStages = currentStages;
        const validApps = (apps as any[]).filter((a: any) => {
                if (
                    !applicationMatchesClientFilters(
                        a,
                        { opdrachtgeverId: filterOpdrachtgever, eindklantId: filterEindklant },
                        undefined,
                        globalVacancies
                    )
                ) {
                    return false;
                }

                if (isClientPortal) {
                    const fallbackVac = globalVacancies?.find((v: any) => v.id === a.vacancy_id);
                    const vac = a.vacancies ?? fallbackVac;
                    if (vac && isPortalArchivedVacancy(vac)) return false;
                    const rawStatus = a.vacancies?.status ?? fallbackVac?.status ?? '';
                    const vacStatus = String(rawStatus).toLowerCase();
                    if (['concept', 'draft', 'afgewezen', 'ingetrokken'].includes(vacStatus)) {
                        return false;
                    }
                    if (portalExcludeApplicationFromPipeline(a, portalPrivacy)) return false;
                }

                if (searchQuery) {
                    const portalApp = { status: a.status, candidates: a.candidates, cv_snapshot: a.cv_snapshot };
                    const portalLabel = portalDisplayCandidateName(portalApp, isClientPortal, portalPrivacy);
                    const rawName =
                        isClientPortal && isPortalHideCandidateNameForStatus(a.status, portalApp, portalPrivacy)
                            ? ''
                            : (a.candidates?.name || '');
                    const vacTitle = a.vacancies?.title || '';
                    if (
                        !searchTextIncludes(portalLabel, searchQuery) &&
                        !searchTextIncludes(rawName, searchQuery) &&
                        !searchTextIncludes(vacTitle, searchQuery)
                    ) {
                        return false;
                    }
                }

                if (filterStatus) {
                    const selected = Array.isArray(filterStatus) ? filterStatus : [filterStatus];
                    if (selected.length > 0) {
                        const candStatus = String(a.status || 'New');
                        const candStatusLower = candStatus.toLowerCase();
                        const ok = selected.some((s) => {
                            const t = String(s || '').trim();
                            if (!t) return false;
                            return candStatus === t || candStatusLower === t.toLowerCase();
                        });
                        if (!ok) return false;
                    }
                }

                return true;
            });

            const newColumns = activeStages.map(stage => {
                // Determine membership
                const stageApps = validApps.filter((a: any) => {

                    // Voorgesteld: ook als DB-status nog vroeg is maar wél portal-publicatie / voorgesteld-datum.
                    // Niet combineren met een latere pipeline-status (bv. al «Gesprek») — voorkomt dubbele kaarten.
                    if ((stage.id === 'Voorgesteld' || stage.name === 'Voorgesteld') && isEffectiveVoorgesteldStage(a)) {
                        if (dbStatusOverridesVoorgesteldKanbanBucket(a.status)) return false;
                        return true;
                    }

                    // 1. Direct match (UUID or Name directly)
                    if (a.status === stage.id || a.status === stage.name) {
                        if ((stage.id === 'Shortlist' || stage.name === 'Shortlist') && isEffectiveVoorgesteldStage(a)) return false;
                        return true;
                    }
                    // 1.5. Bullhorn mapping match
                    const mappedStatus = BULLHORN_STATUS_MAP[a.status];
                    if (mappedStatus === stage.id || mappedStatus === stage.name) {
                        if ((stage.id === 'Shortlist' || stage.name === 'Shortlist') && isEffectiveVoorgesteldStage(a)) return false;
                        return true;
                    }

                    // 2. Legacy 'New' / 'Lead' mapping
                    if ((!a.status || ['new', 'nieuw', 'lead', 'op shortlist'].includes(a.status.toLowerCase())) && (stage.id === 'Shortlist' || stage.name === 'Shortlist')) {
                        return !isEffectiveVoorgesteldStage(a);
                    }
                    
                    // 3. Map Rejected / Closed / klantportaal-afwijzing naar Afgewezen
                    if (stage.id === 'Afgewezen' || stage.name === 'Afgewezen') {
                        const st = String(a.status || '');
                        if (st === 'Rejected' || st === 'Closed' || st === 'Niet aangenomen') return true;
                        if (/afgewezen\s+door\s+opdrachtgever/i.test(st)) return true;
                    }
                    
                    // 4. Map Placed / Hired to Plaatsing
                    if ((a.status === 'Placed' || a.status === 'Hired' || a.status === 'Aangenomen' || a.status === 'Geplaatst' || a.status === 'Bevestigd') && (stage.id === 'Plaatsing' || stage.name === 'Plaatsing')) return true;
                    
                    return false;
                });

                return {
                    id: stage.id,
                    title: stage.name,
                    candidates: stageApps.map((a: any) => {
                        const hideIdentity = isClientPortal && isPortalHideCandidateNameForStatus(a.status, a, portalPrivacy);
                        const portalBlockDetail = isClientPortal && isPortalBlockCandidateDetailForStatus(a.status, a, portalPrivacy);
                        return {
                        id: a.id,
                        candidateId: a.candidate_id,
                        vacancyId: a.vacancy_id,
                        name: portalDisplayCandidateName(
                            { status: a.status, candidates: a.candidates, cv_snapshot: a.cv_snapshot },
                            isClientPortal,
                            portalPrivacy,
                        ),
                        portalBlockDetail,
                        role: id.startsWith('ALL') ? (() => {
                            const vTitle = a.vacancies?.title || 'Open Sollicitatie';
                            const num = a.vacancies?.vacancy_number ? ` (${a.vacancies.vacancy_number})` : '';
                            const loc = a.vacancies?.data?.location || a.vacancies?.data?.city || '';
                            const eindklantName = a.vacancies?.eindklant?.name || a.vacancies?.data?.werkplek || '';
                            const opdrachtgeverName = a.vacancies?.opdrachtgever?.name || a.vacancies?.clients?.name || a.source_client?.name || a.vacancies?.data?.clientName || '';
                            const clientNameStr = eindklantName && opdrachtgeverName && eindklantName !== opdrachtgeverName ? `${eindklantName} (${opdrachtgeverName})` : eindklantName || opdrachtgeverName;
                            const items = [clientNameStr, loc].filter(Boolean).join(', ');
                            return `${vTitle}${num}${items ? ' - ' + items : ''}`;
                        })() : (() => {
                            if (a.candidates?.ai_parsed_data?.work_experience?.[0]) {
                                const exp = a.candidates.ai_parsed_data.work_experience[0];
                                if (exp.role && exp.company) return `${exp.role} bij ${exp.company}`;
                                if (exp.role) return exp.role;
                            }
                            return a.candidates?.role || (a.cv_summary ? 'CV Parsed' : 'Kandidaat');
                        })(),
                        logoUrl: hideIdentity
                            ? null
                            : (a.vacancies?.eindklant?.logo_url || a.candidates?.avatar_url || a.candidates?.photo_url || null),
                        email: hideIdentity ? undefined : getCandidateEmail(a.candidates),
                        phone: hideIdentity
                            ? undefined
                            : (a.candidates?.phone || a.candidates?.ai_parsed_data?.phone || a.candidates?.ai_parsed_data?.personal_info?.phone),
                        vacancyTitle: a.vacancies?.title || a.vacancies?.data?.title || '',
                        companyName: a.vacancies?.eindklant?.name || a.vacancies?.opdrachtgever?.name || a.vacancies?.data?.clientName || a.source_client?.name || '',
                        defaultClientEmail: (() => {
                            const d = a.vacancies?.data || {};
                            return String(
                                d.contactEmail ||
                                    d.clientContactEmail ||
                                    d.contactPersonEmail ||
                                    d.email ||
                                    d.hiringManagerEmail ||
                                    ''
                            ).trim();
                        })(),
                        bullhornId: a.candidates?.ai_parsed_data?.id,
                        candidateNumber: a.candidates?.candidate_number,
                        status: a.status,
                        addedAt: new Date(a.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }),
                        addedAtRaw: new Date(a.created_at).getTime(),
                        matchScore: a.match_score,
                        cvSummary: a.cv_summary,
                        pipelineMeta: (a.cv_snapshot && typeof a.cv_snapshot === 'object') ? (a.cv_snapshot.pipeline_meta || {}) : {},
                        clientConsentBadge: getClientPortalConsentBadgeLabel(a.status),
                        clientPortalDeclineBadge: getClientPortalDeclineBadgeLabel(a.status),
                        portalClientFeedbackLast: getLastPortalClientFeedback(a.cv_snapshot),
                        isDeleted: !a.candidates, // Flag as deleted if join failed
                        sourceClient: a.source_client?.name
                    };
                    }),
                };
            });

        if (Array.isArray(apps)) {
            const mappedCandidateIds = new Set(newColumns.flatMap((c) => c.candidates.map((can) => can.id)));
            const unmapped = validApps.filter((a) => !mappedCandidateIds.has(a.id));
            if (unmapped.length > 0) {
                console.warn(
                    `[KanbanBoard] ${unmapped.length} candidates not mapped to a column. Example statuses:`,
                    [...new Set(unmapped.map((a) => a.status))],
                );
            }
        }

        setColumns(newColumns);
    };

    const loadApplications = async (id: string, currentStages: VacancyStage[] = stages, quiet: boolean = false) => {
        if (!quiet) setIsLoading(true);
        try {
            if (id.startsWith('00000000-0000') && id !== 'ALL') {
                setColumns(currentStages.map((s) => ({ id: s.id, title: s.name, candidates: [] })));
                kanbanRawAppsRef.current = [];
                return;
            }
            const apps = await fetchKanbanApplicationsRaw(id);
            kanbanRawAppsRef.current = apps;
            applyKanbanProjection(apps, id, currentStages);
            setKanbanAppsLoaded((n) => n + 1);
        } catch (e) {
            console.error('Failed to load kanban', e);
        } finally {
            if (!quiet) setIsLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            if (!vacancyId) {
                kanbanRawAppsRef.current = [];
                setColumns(DEFAULT_KANBAN_STAGES.map((s) => ({ id: s.id, title: s.name, candidates: [] })));
                return;
            }
            setIsLoading(true);
            try {
                void fetchVacancyDetails();
                void fetchTemplates();
                const currentStages = await fetchStages(vacancyId);
                if (cancelled) return;
                const apps = await fetchKanbanApplicationsRaw(vacancyId);
                if (cancelled) return;
                kanbanRawAppsRef.current = apps;
                applyKanbanProjection(apps, vacancyId, currentStages);
                setKanbanAppsLoaded((n) => n + 1);
            } catch (e) {
                console.error('[KanbanBoard] init failed', e);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        void init();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kanbanDataFetchKey]);

    /** Zoek/filters: alleen kolommen herberekenen (geen netwerk). */
    useEffect(() => {
        if (!vacancyId || kanbanAppsLoaded === 0) return;
        applyKanbanProjection(kanbanRawAppsRef.current, vacancyId, stages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOpdrachtgever, filterEindklant, filterStatus, searchQuery, stages, vacancyId, kanbanAppsLoaded]);

    // --- DRAG & DROP LOGIC ---

    const handleDragStart = (e: React.DragEvent, candidateId: string) => {
        if (isClientPortal) {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Deze optie is alleen voor werfplaats recruiters.", type: 'error' } }));
            return;
        }
        setDraggedCandidateId(candidateId);
        e.dataTransfer.setData("text/plain", candidateId);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e: React.DragEvent, columnId: string) => {
        e.preventDefault();
        setDragOverColumnId(columnId);
    };

    const handleDrop = async (e: React.DragEvent, targetStatus: string) => {
        e.preventDefault();
        setDragOverColumnId(null);

        const droppedCandidateId = e.dataTransfer.getData("text/plain") || draggedCandidateId;
        if (!droppedCandidateId) return;

        // Find current status & details
        let currentStatus = '';
        let candidateName = '';
        let candidateAppId = '';
        let candidateRealId = ''; // candidate_id from DB

        columns.forEach(col => {
            const found = col.candidates.find(c => c.id === droppedCandidateId || c.candidateId === droppedCandidateId);
            if (found) {
                currentStatus = col.id;
                candidateName = found.name;
                candidateAppId = found.id;
                candidateRealId = found.candidateId;
            }
        });

        if (!currentStatus || currentStatus === targetStatus) {
            setDraggedCandidateId(null);
            return;
        }

        setDraggedCandidateId(null);

        const draggedCandidate = columns.find(c => c.id === currentStatus)?.candidates.find(ca => ca.id === candidateAppId);
        
        // Open Modal to confirm and communicate
        setMoveDetails(
            buildKanbanMoveDetails(
                {
                    id: candidateAppId,
                    candidateId: candidateRealId,
                    name: candidateName,
                    email: draggedCandidate?.email,
                    vacancyTitle: draggedCandidate?.vacancyTitle,
                    companyName: draggedCandidate?.companyName,
                    defaultClientEmail: draggedCandidate?.defaultClientEmail,
                    matchScore: draggedCandidate?.matchScore,
                    cvSummary: draggedCandidate?.cvSummary,
                },
                stages.find(s => s.id === currentStatus)?.name || currentStatus,
                stages.find(s => s.id === targetStatus)?.name || targetStatus,
                { vacancyId: vacancyId || '', clientId: clientId || null },
            ),
        );

        // Reset Modal State
        setShowMoveModal(true);
    };

    const handleMobileMove = (candidateId: string, targetStatus: string) => {
        if (isClientPortal) {
            window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: "Deze optie is alleen voor werfplaats recruiters.", type: 'error' } }));
            return;
        }
        let currentStatus = '';
        let candidateName = '';
        let candidateAppId = '';
        let candidateRealId = '';

        columns.forEach(col => {
            const found = col.candidates.find(c => c.id === candidateId);
            if (found) {
                currentStatus = col.id;
                candidateName = found.name;
                candidateAppId = found.id;
                candidateRealId = found.candidateId;
            }
        });

        if (!currentStatus || currentStatus === targetStatus) return;

        const movedCandidate = columns.find(c => c.id === currentStatus)?.candidates.find(ca => ca.id === candidateId);
        
        setMoveDetails(
            buildKanbanMoveDetails(
                {
                    id: candidateAppId,
                    candidateId: candidateRealId,
                    name: candidateName,
                    email: movedCandidate?.email,
                    vacancyTitle: movedCandidate?.vacancyTitle,
                    companyName: movedCandidate?.companyName,
                    defaultClientEmail: movedCandidate?.defaultClientEmail,
                    matchScore: movedCandidate?.matchScore,
                    cvSummary: movedCandidate?.cvSummary,
                },
                stages.find(s => s.id === currentStatus)?.name || currentStatus,
                stages.find(s => s.id === targetStatus)?.name || targetStatus,
                { vacancyId: vacancyId || '', clientId: clientId || null },
            ),
        );

        setShowMoveModal(true);
    };

    const handleConfirmMove = async (
        channel: 'email' | 'whatsapp' | 'log',
        templateId: string,
        subject: string,
        messageContent: string,
        internalNoteContent: string,
        targetStage: string,
        asDraft: boolean = false,
        rejectionReason?: string,
        cc?: string,
        bcc?: string,
        fromEmail?: string,
        voorgesteldClient?: VoorgesteldClientPayload | null,
        resolvedCandidateEmail?: string,
    ) => {
        if (!moveDetails) return;
        setIsMoving(true);

        try {
            // Update local memory to ensure we use targetStage properly
            const finalToStage = targetStage || (targetStage || moveDetails.toStage);
            const finalCandidateRealId = moveDetails.candidateRealId || moveDetails.candidateId;

            // 1. Update Application Status
            await supabase_update_application_status(moveDetails.candidateId, stages.find(s => s.name === finalToStage || s.id === finalToStage)?.id || 'New');

            // If moved into "Voorgesteld", auto-set voorgesteld_date to today's date (date of drag/drop)
            const toStageObj = stages.find(s => s.name === finalToStage || s.id === finalToStage);
            if (toStageObj && (toStageObj.name === 'Voorgesteld' || toStageObj.id === 'Voorgesteld')) {
                const todayIso = new Date().toISOString().slice(0, 10);
                await updateApplicationPipelineMeta(moveDetails.candidateId, { voorgesteld_date: todayIso });
            }

            const isVoorgesteld = toStageObj && (toStageObj.name === 'Voorgesteld' || toStageObj.id === 'Voorgesteld');
            const portalVid = String(vacancyId || moveDetails.vacancyId || '').trim();
            if (isVoorgesteld && voorgesteldClient && !asDraft) {
                if (!voorgesteldClient.publishToPortal) {
                    const { error: msErr } = await supabase_recruiter_apply_voorgesteld_match_score(
                        moveDetails.candidateId,
                        voorgesteldClient.portalMatchScore,
                    );
                    if (msErr) console.error('[KanbanBoard] match_score sync', msErr);
                }
                if (voorgesteldClient.publishToPortal) {
                    const { error: pubErr } = await supabase_recruiter_publish_portal_proposal(
                        moveDetails.candidateId,
                        portalVid,
                        voorgesteldClient,
                        {
                            recruiterName: currentUser?.profile?.name || currentUser?.email || 'Recruiter',
                            clientId: moveDetails.clientId,
                            candidateRealId: moveDetails.candidateRealId || moveDetails.candidateId,
                        }
                    );
                    if (pubErr) {
                        console.error('[KanbanBoard] portal publish', pubErr);
                        alert('Voorstel kon niet worden opgeslagen in het klantportaal. Controleer rechten of probeer opnieuw.');
                    }
                }
                if (voorgesteldClient.emailToClient && voorgesteldClient.clientEmailTo?.trim()) {
                    try {
                        const { sendDirectMessage } = await import('../services/microsoftGraphService');
                        let personalizedBody = voorgesteldClient.clientEmailBodyHtml.replace(/\n/g, '<br>');
                        const isSignatureInBody =
                            clientEmailSignature &&
                            personalizedBody.replace(/\s/g, '').includes(clientEmailSignature.replace(/\s/g, '').substring(0, 20));
                        if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
                            personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, clientEmailSignature || '');
                        } else if (clientEmailSignature && !isSignatureInBody) {
                            personalizedBody += `<br><br>${clientEmailSignature}`;
                        }
                        const rawTo = voorgesteldClient.clientEmailTo.split(',').map((s) => s.trim()).filter(Boolean);
                        const primary = rawTo[0];
                        const ccFromPrimaryField = rawTo.slice(1);
                        const ccFromPayload = (voorgesteldClient.clientEmailCc || '')
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean);
                        const ccArr = [...ccFromPrimaryField, ...ccFromPayload, ...(cc ? cc.split(',').map((s) => s.trim()).filter(Boolean) : [])];
                        const bccFromPayload = (voorgesteldClient.clientEmailBcc || '')
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean);
                        const bccArr = [...bccFromPayload, ...(bcc ? bcc.split(',').map((s) => s.trim()).filter(Boolean) : [])];
                        if (!primary) {
                            alert('Geen geldig klant-e-mailadres ingevuld.');
                        } else {
                            const { success, error } = await sendDirectMessage(
                                voorgesteldClient.clientEmailSubject,
                                personalizedBody,
                                primary,
                                ccArr,
                                bccArr,
                                fromEmail || undefined
                            );
                            if (!success) {
                                if (error?.message?.includes('does not have the right to send mail')) {
                                    alert(
                                        `Je hebt geen Send As voor ${fromEmail || 'deze mailbox'}. Kies je eigen mailbox of vraag IT.`
                                    );
                                } else {
                                    alert('E-mail naar klant mislukt.');
                                }
                            }
                        }
                    } catch (e) {
                        console.error('[KanbanBoard] client proposal email', e);
                        alert('Fout bij e-mail naar klant.');
                    }
                }
            }

            // 2. Trigger Automation (New)
            const targetStageObj = stages.find(s => s.name === finalToStage || s.id === finalToStage);
            if (targetStageObj) {
                const autoPayload = {
                    vacancyId: vacancyId || 'GLOBAL',
                    candidateId: moveDetails.candidateRealId || moveDetails.candidateId,
                    stageId: targetStageObj.id,
                    stageName: targetStageObj.name,
                    user_id: (await supabase.auth.getUser()).data.user?.id
                };

                // ALWAYS trigger stage_changed
                await triggerAutomation('stage_changed', autoPayload);

                // Specific triggers for certain stage types
                let triggerType = null;
                if (targetStageObj.type === 'Interview') triggerType = 'status_interview';
                if (targetStageObj.type === 'Hired') triggerType = 'candidate_hired';

                if (triggerType) {
                    await triggerAutomation(triggerType as any, autoPayload);
                }
            }

            // 3. Log Interaction / Send Communication
            if (channel === 'email' && messageContent) {
                const { data: { user } } = await supabase.auth.getUser();
                try {
                    let personalizedBody = messageContent.replace(/\n/g, '<br>');
                    const isSignatureInBody = clientEmailSignature && personalizedBody.replace(/\s/g, '').includes(clientEmailSignature.replace(/\s/g, '').substring(0, 20));
                    
                    if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
                        personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, clientEmailSignature || '');
                    } else if (clientEmailSignature && !isSignatureInBody) {
                        personalizedBody += `<br><br>${clientEmailSignature}`;
                    }

                    // Dynamically import MS graph logic instead of requiring top-level if not already used
                    const { createDraftMessage, sendDirectMessage } = await import('../services/microsoftGraphService');
                    const candEmail = (resolvedCandidateEmail || moveDetails.candidateEmail || '').trim();

                    if (!candEmail) {
                        alert("Geen e-mailadres gevonden voor deze kandidaat.");
                        setIsMoving(false);
                        return;
                    }

                    const ccArr = cc ? cc.split(',').map(s => s.trim()).filter(Boolean) : [];
                    const bccArr = bcc ? bcc.split(',').map(s => s.trim()).filter(Boolean) : [];

                    if (asDraft) {
                        const { success } = await createDraftMessage(subject, personalizedBody, candEmail, ccArr, bccArr, fromEmail || undefined);
                        if (success) setToastMessage("Concept e-mail succesvol aangemaakt in Outlook!");
                        else alert("Het aanmaken van een concept in Outlook is mislukt.");
                    } else {
                        const { success, error } = await sendDirectMessage(subject, personalizedBody, candEmail, ccArr, bccArr, fromEmail || undefined);
                        if (success) setToastMessage("E-mail succesvol verzonden via Outlook!");
                        else {
                            if (error?.message?.includes('does not have the right to send mail')) {
                                alert(`Je hebt in Microsoft 365 geen "Verzenden Als" (Send As) rechten gekregen om te mailen vanuit de gedeelde mailbox: ${fromEmail}. Vraag je IT-beheerder om dit in te stellen of kies je eigen mailbox.`);
                            } else {
                                alert("Het verzenden via Outlook is mislukt.");
                            }
                        }
                    }
                    setTimeout(() => setToastMessage(''), 4000);
                } catch(e) {
                    console.error("M365 Send Error", e);
                    alert("Fout bij E-mail / M365 connectie");
                }
            } else if (channel === 'whatsapp' && messageContent) {
                if (asDraft) {
                    const { data: { user } } = await supabase.auth.getUser();
                    await supabase.from('todos').insert({
                        title: `[OUTBOX WhatsApp] Bericht aan ${moveDetails.candidateName}`,
                        description: messageContent,
                        status: 'Pending',
                        due_date: new Date().toISOString(),
                        user_id: user?.id,
                        assignee_id: user?.id
                    });
                    setToastMessage('WhatsApp bericht is opgeslagen in je Postvak UIT.');
                    setTimeout(() => setToastMessage(''), 4000);
                } else {
                    const textUrl = encodeURIComponent(messageContent);
                    window.open(`https://web.whatsapp.com/send?text=${textUrl}`, '_blank');
                    setToastMessage('WhatsApp web wordt geopend.');
                    setTimeout(() => setToastMessage(''), 3000);
                }
            } else {
                setToastMessage('Kandidaat succesvol verplaatst.');
                setTimeout(() => setToastMessage(''), 3000);
            }

            // Register the pipeline movement in the Activities timeline
            const finalTargetStage = targetStage || moveDetails.toStage;
            
            // 1. Log the pipeline change event
            const finalInternalNote = rejectionReason ? `${internalNoteContent}\n\nAfwijzingsreden: ${rejectionReason}`.trim() : internalNoteContent;
            
            if (clientId && currentUser) {
                const nameStr = currentUser.profile?.name || currentUser.name || 'Recruiter';
                
                await supabase_log_interaction(
                    moveDetails.candidateRealId,
                    clientId,
                    'system',
                    `(Status Update) ${nameStr} heeft kandidaat verplaatst van '${moveDetails.fromStage}' naar '${(targetStage || moveDetails.toStage)}'.${rejectionReason ? ` Reden: ${rejectionReason}` : ''}`
                );

                // 2. Log the feedback note specifically meant for this move
                if (finalInternalNote && finalInternalNote.trim()) {
                    await supabase_log_interaction(
                        moveDetails.candidateRealId,
                        clientId,
                        'note',
                        `[Verplaatst naar ${(targetStage || moveDetails.toStage)}] ${finalInternalNote}`
                    );
                }
                
                // Track rejection reason globally into ai_parsed_data for Talent Pool
                if (rejectionReason) {
                    const { data: profile } = await supabase.from('candidates').select('ai_parsed_data').eq('id', moveDetails.candidateRealId).single();
                    if (profile) {
                        const existingData = profile.ai_parsed_data || {};
                        await supabase.from('candidates').update({
                            ai_parsed_data: { ...existingData, rejectionReason: rejectionReason }
                        }).eq('id', moveDetails.candidateRealId);
                    }
                }
            }

            // If an explicit external message was sent to the candidate, log it too in Activity
            if (clientId && channel !== 'log' && messageContent) {
                const logType = channel === 'whatsapp' ? 'whatsapp' : 'email';
                await supabase_log_interaction(
                    moveDetails.candidateRealId,
                    clientId,
                    logType,
                    `[Inkomend/Uitgaand: ${(targetStage || moveDetails.toStage)}] ${messageContent}`
                );
            }

            // 3. Optimistic UI Update
            setColumns(prevCols => {
                const candidateToMove = prevCols.flatMap((c) => c.candidates).find((c) => c.id === moveDetails.candidateId);
                const destLabel = targetStage || moveDetails.toStage;
                const destStageObj = stages.find((s) => s.name === destLabel || s.id === destLabel);
                const nextStatusId = destStageObj?.id || destLabel;
                return prevCols.map((col) => {
                    const isSource = col.title === moveDetails.fromStage || col.id === moveDetails.fromStage;
                    if (isSource) {
                        return { ...col, candidates: col.candidates.filter((c) => c.id !== moveDetails.candidateId) };
                    }
                    if (
                        col.title === destLabel ||
                        col.id === destLabel ||
                        (destStageObj && (col.id === destStageObj.id || col.title === destStageObj.name))
                    ) {
                        if (candidateToMove) {
                            return {
                                ...col,
                                candidates: [
                                    ...col.candidates,
                                    {
                                        ...candidateToMove,
                                        status: nextStatusId,
                                        clientConsentBadge: getClientPortalConsentBadgeLabel(nextStatusId),
                                        clientPortalDeclineBadge: getClientPortalDeclineBadgeLabel(nextStatusId),
                                    },
                                ],
                            };
                        }
                    }
                    return col;
                });
            });

            // Re-fetch to be safe
            if (vacancyId) loadApplications(vacancyId);

        } catch (e) {
            console.error("Move failed", e);
            alert("Er ging iets mis bij het verplaatsen.");
        } finally {
            setIsMoving(false);
            setShowMoveModal(false);
            setMoveDetails(null);
        }
    };

    const handleDeleteApplication = async (applicationId: string) => {
        if (!applicationId) return;
        if (isClientPortal) {
            window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Verwijderen is niet beschikbaar in het clientportaal.', type: 'error' } }));
            return;
        }
        setDeleteAppId(applicationId);
    };

    const confirmDeleteApplication = async () => {
        if (!deleteAppId) return;
        if (isClientPortal) {
            setDeleteAppId(null);
            return;
        }
        try {
            const { error } = await supabase.from('applications').delete().eq('id', deleteAppId);
            if (error) throw error;
            // Optimistic UI Update
            setColumns(prevCols => prevCols.map(col => ({
                ...col,
                candidates: col.candidates.filter(c => c.id !== deleteAppId)
            })));
            setToastMessage("Kandidaat succesvol verwijderd.");
            setTimeout(() => setToastMessage(''), 3000);
            if (vacancyId) loadApplications(vacancyId);
        } catch (e) {
            console.error("Delete failed", e);
            alert("Er ging iets mis bij het verwijderen.");
        } finally {
            setDeleteAppId(null);
        }
    };

    // --- AI Match ENHANCED ---
    const handleAiMatch = async (candidate: any) => {
        if (!vacancyId || !candidate) return;
        setIsAiMatching(true);
        setAiMatchCandidateId(candidate.id);

        try {
            // 1. Fetch FULL Profile Data (Deep Match)
            // Ensure we have the client ID context
            const currentClientId = clientId || '00000000-0000-0000-0000-000000000001';
            const fullProfile = await supabase_get_candidate_profile_by_id(candidate.candidateId, currentClientId);

            const vac = { title: vacancyTitle, description: '...', data: {} }; // Ideally fetch full vacancy too

            // If no full profile, fallback to current data
            const analysisData = fullProfile ? {
                name: (fullProfile as any).candidates?.name || fullProfile.candidate?.name || candidate.name,
                role: (fullProfile as any).candidates?.role || fullProfile.candidate?.role || candidate.role,
                summary: fullProfile.ai_parsed_data?.summary || candidate.cvSummary,
                skills: fullProfile.ai_parsed_data?.tags || [],
                timeline: fullProfile.ai_parsed_data?.timeline || []
            } : {
                name: candidate.name,
                role: candidate.role,
                summary: candidate.cvSummary,
                skills: [],
                timeline: []
            };

            // Perform Match
            const result = await performRealMatch(analysisData, vac);
            setAiAnalysis(result);
            setShowAiModal(true);
        } catch (e) {
            console.error(e);
            alert("AI Match mislukt. Controleer internetverbinding.");
        } finally {
            setIsAiMatching(false);
            setAiMatchCandidateId(null);
        }
    };
    // ... (Keep existing handlers for File Upload, Pool Add, Reviews, Invites - they work fine)
    // Re-including critical handlers for compilation:
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setUploadFile(file);
            setIsAnalyzing(true);
            try {
                const base64 = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
                    reader.readAsDataURL(file);
                });
                const analysis = await analyzeUploadedCV({ base64, mimeType: file.type });
                setParsedCandidate(analysis);
            } catch (e) {
                console.error(e);
                alert("Kon CV niet analyseren.");
            } finally {
                setIsAnalyzing(false);
            }
        }
    };

    const handleSaveNewCandidate = async () => {
        if (!parsedCandidate || !vacancyId || !clientId) return;
        setIsSavingNew(true);
        try {
            await supabase_submit_application(vacancyId, clientId, {
                firstName: parsedCandidate.firstName || 'Kandidaat',
                lastName: parsedCandidate.lastName || '',
                email: parsedCandidate.email,
                cvSummary: parsedCandidate.summary,
                matchScore: parsedCandidate.matchScore,
                parsedData: parsedCandidate,
                file: uploadFile,
                motivation: "Handmatig toegevoegd via Recruitment Flow.",
                source: candidateSource
            });
            setShowAddModal(false);
            setParsedCandidate(null);
            setUploadFile(null);
            setCandidateSource('Handmatig');
            loadApplications(vacancyId);
        } catch (e) {
            alert("Fout bij opslaan.");
        } finally {
            setIsSavingNew(false);
        }
    };

    const fetchPoolAndVacancies = async () => {
        setIsPoolLoading(true);
        const isGlobal = clientId === '00000000-0000-0000-0000-000000000001' || !clientId;
        try {
            // Pool is no longer fetched on load; it's fetched lazily when the user types.
            setPoolCandidates([]);
            

            let query = supabase.from('vacancies').select('id, title, status, vacancy_number, data, client_id, clients!vacancies_client_id_fkey(name, logo_url)');
            if (!isGlobal) {
                query = query.eq('client_id', clientId);
            }
            
            const { data: vacs } = await query;
                
            if (vacs) {
                setAvailableVacancies(vacs.sort((a: any, b: any) => a.title.localeCompare(b.title)));
            }

            if (vacancyId && !vacancyId.startsWith('ALL') && !vacancyId.startsWith('0000')) {
                setTargetAddVacancyId(vacancyId);
                const matched = vacs.find((v:any) => v.id === vacancyId);
                if (matched) setVacancySearchQuery(matched.title);
                
                // Specific vacancy -> add to Shortlist by default
                const shortlistId = DEFAULT_KANBAN_STAGES.find(s => s.name === 'Shortlist')?.id || DEFAULT_KANBAN_STAGES[0].id;
                setTargetAddStageId(shortlistId);
            } else {
                setTargetAddVacancyId('');
                setVacancySearchQuery('');
                const shortlistId = DEFAULT_KANBAN_STAGES.find(s => s.name === 'Shortlist')?.id || DEFAULT_KANBAN_STAGES[0].id;
                setTargetAddStageId(shortlistId);
            }
            
        } catch (e) { console.error(e); } finally { setIsPoolLoading(false); }
    };

    const handleAddFromPool = async () => {
        if (selectedPoolIds.length === 0 || !targetAddVacancyId || !targetAddStageId) return;
        
        // Find correct client ID for global context
        let activeClientId = clientId;
        const selectedVac = availableVacancies.find(v => v.id === targetAddVacancyId);
        if (selectedVac && selectedVac.client_id) {
            activeClientId = selectedVac.client_id;
        }
        
        if (!activeClientId || activeClientId === '00000000-0000-0000-0000-000000000001') {
            alert("Kan geen klant-ID bepalen voor de geselecteerde vacature.");
            return;
        }

        setIsSavingNew(true);
        try {
            const toInsert = selectedPoolIds.map(cid => ({
                vacancy_id: targetAddVacancyId,
                candidate_id: cid,
                status: targetAddStageId
            }));

            const { error } = await supabase.from('applications').insert(toInsert);
            if (error) {
                console.error("Insert error:", error);
                throw error;
            }
            setShowAddModal(false);
            setSelectedPoolIds([]);
            
            // Only reload if we added to the current viewed vacancy
            if (vacancyId === targetAddVacancyId || vacancyId === 'ALL_OPEN' || vacancyId?.startsWith('ALL_')) {
                loadApplications(vacancyId);
            }
        } catch (e) {
            console.error(e);
            alert('Fout bij toevoegen uit Talentpool');
        } finally {
            setIsSavingNew(false);
        }
    };

    const handleAiPoolSearch = async () => {
        if (!poolCandidates.length || !poolSearchQuery.trim()) return;
        setIsPoolSearching(true);
        try {
            const result = await matchTalentPool(poolSearchQuery, poolCandidates);
            setAiMatchedPoolIds(result.matchedIds || []);
            setAiMatchReasoning(result.reasoning || '');
        } catch (e) {
            console.error(e);
            alert("AI Match mislukt.");
        } finally {
            setIsPoolSearching(false);
        }
    };

    const handleInviteReviewer = async () => {
        if (!inviteEmail || !vacancyId) return;
        setIsInviting(true);
        try {
            await supabase_invite_vacancy_reviewer(vacancyId, inviteEmail, 'Reviewer');
            alert(`Uitnodiging verstuurd naar ${inviteEmail}`);
            setShowInviteModal(false);
            setInviteEmail('');
        } catch (e) { alert("Kon uitnodiging niet versturen."); } finally { setIsInviting(false); }
    };

    const openReviewModal = (candidate: any) => {
        setReviewCandidate(candidate);
        setReviewScore(0);
        setReviewComment('');
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        if (!reviewCandidate || !vacancyId) return;
        setIsSavingReview(true);
        try {
            await supabase_save_candidate_review(vacancyId, reviewCandidate.candidateId, 'current-user@werftools.nl', reviewScore, reviewComment);
            alert("Beoordeling opgeslagen!");
            setShowReviewModal(false);
        } catch (e) { alert("Kon review niet opslaan."); } finally { setIsSavingReview(false); }
    };

    const handleFeedbackClick = async (candidate: any) => {
        // Find the stage this candidate is currently residing in
        let currentStageId = '';
        columns.forEach(col => {
            if (col.candidates.some(c => c.id === candidate.id)) {
                currentStageId = col.id;
            }
        });
        const stage = stages.find(s => s.id === currentStageId || s.name === currentStageId);

        // If stage requires scorecard
        if (stage?.requirements?.scorecard) {
            // Find interview
            // We need candidate REAL ID. `candidate` object has `candidateId`.
            const { data, error } = await supabase
                .from('interviews')
                .select('id')
                .eq('id', candidate.candidateId)
                .eq('stage_id', stage.id)
                .order('scheduled_at', { ascending: false })
                .limit(1)
                .single();

            if (data) {
                setFeedbackSession({
                    interviewId: data.id,
                    stageId: stage.id,
                    candidateName: candidate.name
                });
                setShowFeedbackModal(true);
            } else {
                if (confirm("Geen interview gevonden voor deze fase. Wil je er nu een inplannen?")) {
                    setScheduleCandidate({
                        applicationId: candidate.id,
                        candidateId: candidate.candidateId,
                        name: candidate.name,
                        stageId: stage.id,
                        vacancyId: candidate.vacancyId,
                    });
                    setShowScheduler(true);
                }
            }
        } else {
            // Standard Review
            openReviewModal(candidate);
        }
    };

    // Skeleton Loader Component
    const KanbanSkeleton = () => (
        <div className="flex gap-6 overflow-x-auto pb-6 h-full items-start min-w-full">
            {stages.map(s => (
                <div key={s.id} className="w-80 shrink-0 flex flex-col h-full max-h-[calc(100vh-200px)] bg-slate-100/50 rounded-xl border border-slate-200/50">
                    <div className="p-4 flex items-center gap-2 border-b border-slate-200/50 bg-white/50 rounded-t-xl">
                        <div className="w-2 h-2 rounded-full bg-slate-200 animate-pulse"></div>
                        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse"></div>
                    </div>
                    <div className="p-3 space-y-3">
                        {[1, 2].map(i => (
                            <div key={i} className="bg-white p-4 rounded-lg border border-slate-100 shadow-sm h-32 animate-pulse"></div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    if (isLoading) {
        return <KanbanSkeleton />;
    }

    const activeColumn = columns.find(c => c.id === activeStage);

    return (
        <div className="h-full flex flex-col relative bg-transparent">
            {/* Top Control Bar (Ashby Style) */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 shrink-0 z-10">
                {/* Left: Empty space for alignment to replace removed filter buttons */}
                <div className="flex-1"></div>

                {/* Right: Actions */}
                <div className="flex items-center gap-2 self-end sm:self-auto">
                    {onAnalytics && (
                        <button onClick={onAnalytics} className="flex items-center justify-center p-2 bg-white border border-slate-200 text-slate-400 hover:text-slate-600 rounded-lg transition-colors shadow-sm" title="Analytics">
                            <BarChart2 size={15} />
                        </button>
                    )}
                    {onConfigFlow && (
                        <button onClick={onConfigFlow} className="flex items-center justify-center p-2 bg-white border border-slate-200 text-slate-400 hover:text-slate-600 rounded-lg transition-colors shadow-sm" title="Configuratie">
                            <Settings size={15} />
                        </button>
                    )}
                    <div className="w-px h-6 bg-slate-200 mx-1"></div>
                    {!isReviewerOnly && userRole !== UserRole.VACANCY_REVIEWER && (
                        <>
                            <button onClick={() => { setIsBulkMode(!isBulkMode); setSelectedBulkCandidateIds([]); }} className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg transition-colors shadow-sm ${isBulkMode ? 'bg-indigo-100 text-indigo-700' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                                <CheckCircle2 size={14} /> Bulk Select
                            </button>
                            {isBulkMode && selectedBulkCandidateIds.length > 0 && (
                                <>
                                    <button onClick={() => setShowBulkWhatsApp(true)} className="px-3 py-2 text-xs font-bold text-white bg-[#25D366] hover:bg-[#128C7E] rounded-lg transition-colors shadow-sm flex items-center gap-1.5">
                                        <Phone size={14} /> {selectedBulkCandidateIds.length > 1 ? 'Samen Appen' : 'Appen'} ({selectedBulkCandidateIds.length})
                                    </button>
                                    <button onClick={() => setShowBulkEmail(true)} className="px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5">
                                        <Mail size={14} /> {selectedBulkCandidateIds.length > 1 ? 'Samen Mailen' : 'Mailen'} ({selectedBulkCandidateIds.length})
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => {
                                    if (onCustomAddCandidateClick) {
                                        onCustomAddCandidateClick();
                                    } else {
                                        setShowAddModal(true); setAddMode('pool'); fetchPoolAndVacancies();
                                    }
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-[#0073e6] hover:bg-[#005bb5] text-white rounded-lg text-xs font-bold transition-all shadow-sm"
                            >
                                <Plus size={14} /> Kandidaat toevoegen
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* True Kanban Scrolling Board */}
            <div className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar -mx-4 px-4 sm:-mx-6 sm:px-6 snap-x snap-mandatory">
                <div className="flex gap-4 h-full pb-32 lg:pb-6 items-start min-w-max">
                    {stages.map((stage, sIdx) => {
                        const col = columns.find(c => c.id === stage.id);
                        if (!col) return null;
                        
                        // Filter candidates based on segment
                        const visibleCandidates = col.candidates.filter(c => filterType === 'Qualified' ? !c.isDeleted : c.isDeleted);
                        const count = visibleCandidates.length;
                        
                        // Generate color palette for stages based on index/type to mimic Ashby
                        const getStageColor = (idx: number, type: string) => {
                             if (type === 'Hired' || type === 'Offer') return 'bg-emerald-500';
                             if (idx === 0) return 'bg-slate-500';
                             const colors = ['bg-blue-400', 'bg-blue-600', 'bg-indigo-500', 'bg-violet-500', 'bg-fuchsia-500'];
                             return colors[(idx - 1) % colors.length] || 'bg-slate-400';
                        };
                        const stageColor = getStageColor(sIdx, stage.type);
                        const isDragOver = dragOverColumnId === stage.id;
                        
                        // Apply searches and sorting
                        let finalCandidates = [...visibleCandidates];
                        const searchQ = columnSearch[stage.id] || '';
                        if (searchQ) {
                            finalCandidates = finalCandidates.filter(
                                (c) =>
                                    searchTextIncludes(c.name, searchQ) ||
                                    searchTextIncludes(c.role, searchQ) ||
                                    foldSearchText(String(c.bullhornId ?? '')).includes(foldSearchText(searchQ)),
                            );
                        }
                        
                        const sortType = columnSort[stage.id] || 'default';
                        if (sortType === 'az') {
                            finalCandidates.sort((a, b) => a.name.localeCompare(b.name));
                        } else if (sortType === 'date') {
                            finalCandidates.sort((a, b) => (b.addedAtRaw || 0) - (a.addedAtRaw || 0));
                        }

                        return (
                            <div 
                                key={stage.id} 
                                className={`w-[85vw] sm:w-[290px] shrink-0 h-full flex flex-col transition-all rounded-xl snap-center snap-always ${isDragOver ? 'bg-slate-100/80 ring-2 ring-dashed ring-slate-300' : ''}`}
                                onDragOver={(e) => handleDragOver(e, stage.id)}
                                onDrop={(e) => handleDrop(e, stage.id)}
                            >
                                {/* Column Header */}
                                    <div className="flex justify-between items-center px-1 sticky top-0 z-10 bg-[#f8fafc]/90 backdrop-blur-sm pt-2 pb-1">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2.5 h-2.5 rounded-[2px] ${stageColor}`}></div>
                                            <h3 className="text-[13px] font-bold text-slate-800">{stage.name}</h3>
                                            <span className="text-[11px] font-semibold text-slate-400 ml-1">{count}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-slate-400">
                                            <button onClick={() => setColumnSort(prev => ({ ...prev, [stage.id]: prev[stage.id] === 'az' ? 'date' : (prev[stage.id] === 'date' ? 'default' : 'az') }))} className={`p-1 hover:text-slate-600 transition-colors ${columnSort[stage.id] && columnSort[stage.id] !== 'default' ? 'text-indigo-500' : ''}`} title={columnSort[stage.id] === 'az' ? 'Gesorteerd A-Z' : (columnSort[stage.id] === 'date' ? 'Gesorteerd op datum' : 'Standaard sortering')}>
                                                {columnSort[stage.id] === 'az' ? <span className="text-[10px] font-bold">AZ</span> : (columnSort[stage.id] === 'date' ? <Calendar size={13} /> : <MoreHorizontal size={13} />)}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Column Quick Search */}
                                    <div className="px-2 pb-3 sticky top-10 z-10 bg-[#f8fafc]/90 backdrop-blur-sm">
                                        <div className="relative">
                                            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <input 
                                                type="text" 
                                                placeholder="Zoeken..." 
                                                value={columnSearch[stage.id] || ''}
                                                onChange={(e) => setColumnSearch(prev => ({ ...prev, [stage.id]: e.target.value }))}
                                                className="w-full pl-7 pr-3 py-1.5 bg-white border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                    </div>

                                {/* Column Cards Container */}
                                <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar space-y-3 px-1 pb-10">
                                    {finalCandidates.map((candidate) => {
                                        const isUnreadNew = sIdx === 0 && !openedCandidateIds.has(candidate.id);
                                        return (
                                            <div
                                                key={candidate.id}
                                                draggable={!isClientPortal}
                                                onDragStart={(e) => handleDragStart(e, candidate.id)}
                                                onClick={() => {
                                                    if (isBulkMode) {
                                                        toggleBulkSelect(candidate.id);
                                                    } else if (candidate.portalBlockDetail) {
                                                        window.dispatchEvent(
                                                            new CustomEvent('wt:toast', {
                                                                detail: {
                                                                    message: 'Details voor deze fase zijn niet beschikbaar in het clientportaal.',
                                                                    type: 'error',
                                                                },
                                                            }),
                                                        );
                                                    } else {
                                                        markAsOpened(candidate.id);
                                                        setSelectedCandidateId(candidate.candidateId);
                                                        setShowDetailModal(true);
                                                    }
                                                }}
                                                className={`bg-white p-3.5 rounded-[12px] border shadow-none hover:border-slate-300 transition-all ${
                                                    isClientPortal ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
                                                } group relative flex flex-col ${
                                                    draggedCandidateId === candidate.id ? 'opacity-50 scale-95' : 'opacity-100'
                                                } border-slate-200/70 ${
                                                    isAiMatching && aiMatchCandidateId === candidate.id ? 'ring-1 ring-purple-500 border-purple-500' : ''
                                                }`}
                                            >
                                                {/* Top Row: Avatar + Name */}
                                                <div className="flex items-center gap-3 mb-3">
                                                    {isBulkMode && (
                                                        <div className="absolute top-3 right-3 flex items-center justify-center z-10">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selectedBulkCandidateIds.includes(candidate.id)} 
                                                                onChange={() => {}} 
                                                                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer pointer-events-none" 
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-600 border border-slate-200 shrink-0 uppercase overflow-hidden">
                                                        {candidate.logoUrl ? (
                                                            <img src={candidate.logoUrl} alt="Logo" className="w-full h-full object-contain p-0.5 bg-white" />
                                                        ) : (
                                                            candidate.name.substring(0, 2)
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h5 className="font-bold text-slate-900 text-[13px] truncate">{candidate.name}</h5>
                                                        <p className="text-[10px] text-slate-600 font-semibold truncate mt-0.5 max-w-full overflow-hidden text-ellipsis whitespace-nowrap flex items-center">
                                                            {candidate.candidateNumber ? <span className="font-mono text-slate-400 font-bold mr-1.5 shrink-0">#{candidate.candidateNumber}</span> : null}
                                                            <span className="truncate">{candidate.role ? candidate.role.split(' - ')[0] : candidate.email}</span>
                                                        </p>
                                                        {candidate.role && candidate.role.includes(' - ') && (
                                                            <p className="text-[9.5px] text-slate-400 font-medium truncate mt-0.5 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                                                {candidate.role.split(' - ').slice(1).join(' - ')}
                                                            </p>
                                                        )}
                                                        {candidate.clientPortalDeclineBadge ? (
                                                            <span className="mt-1.5 inline-flex max-w-full items-center rounded-md bg-amber-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-900 ring-1 ring-amber-200">
                                                                {candidate.clientPortalDeclineBadge}
                                                            </span>
                                                        ) : null}
                                                        {candidate.clientConsentBadge ? (
                                                            <span className="mt-1.5 inline-flex max-w-full items-center rounded-md bg-sky-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-800 ring-1 ring-sky-100">
                                                                {candidate.clientConsentBadge}
                                                            </span>
                                                        ) : null}
                                                        {candidate.clientPortalDeclineBadge ? (
                                                            <div
                                                                className="mt-2 rounded-md border border-amber-200/80 bg-amber-50/90 px-2 py-1.5 text-left"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <p className="text-[9px] font-black uppercase tracking-wide text-amber-900">
                                                                    Toelichting klant (portaal)
                                                                </p>
                                                                <p className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words text-[10px] font-medium leading-snug text-amber-950">
                                                                    {candidate.portalClientFeedbackLast?.text ||
                                                                        'Geen reactietekst bijgevoegd in het portaal. Je kunt de kandidaat alsnog zelf naar Afgewezen verplaatsen.'}
                                                                </p>
                                                                {candidate.portalClientFeedbackLast?.at ? (
                                                                    <p className="mt-1 text-[9px] text-amber-800/80">
                                                                        {new Date(candidate.portalClientFeedbackLast.at).toLocaleString('nl-NL')}
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                        {/* Gesprek / Voorgesteld metadata */}
                                                        {!isClientPortal && (stage.name === 'Gesprek' || stage.id === 'Gesprek' || stage.name === 'Voorgesteld' || stage.id === 'Voorgesteld') && (
                                                            <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()}>
                                                                {formatPipelineInterviewHint(candidate.pipelineMeta as Record<string, unknown> | undefined) ? (
                                                            <p className="text-[9px] font-bold text-violet-700">
                                                                {formatPipelineInterviewHint(candidate.pipelineMeta as Record<string, unknown> | undefined)}
                                                            </p>
                                                        ) : null}
                                                        {stage.name === 'Gesprek' || stage.id === 'Gesprek' ? (
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Gesprek</span>
                                                                        <select
                                                                            value={candidate.pipelineMeta?.gesprek_round || '1'}
                                                                            onChange={(e) => updateApplicationPipelineMeta(candidate.id, { gesprek_round: e.target.value })}
                                                                            className="text-[9px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none"
                                                                            title="Gespreksronde"
                                                                        >
                                                                            <option value="1">1e</option>
                                                                            <option value="2">2e</option>
                                                                            <option value="3">3e</option>
                                                                        </select>
                                                                        <input
                                                                            type="date"
                                                                            value={(candidate.pipelineMeta?.gesprek_date || '').slice(0, 10)}
                                                                            onChange={(e) => updateApplicationPipelineMeta(candidate.id, { gesprek_date: e.target.value })}
                                                                            className="text-[9px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none"
                                                                            title="Gespreksdatum"
                                                                        />
                                                                        {isUpdatingMeta[candidate.id] && <Loader2 size={12} className="animate-spin text-slate-400" />}
                                                                    </div>
                                                                ) : null}
                                                                {stage.name === 'Voorgesteld' || stage.id === 'Voorgesteld' ? (
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Voorgesteld</span>
                                                                        <input
                                                                            type="date"
                                                                            value={(candidate.pipelineMeta?.voorgesteld_date || '').slice(0, 10)}
                                                                            onChange={(e) => updateApplicationPipelineMeta(candidate.id, { voorgesteld_date: e.target.value })}
                                                                            className="text-[9px] font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none"
                                                                            title="Datum voorgesteld"
                                                                        />
                                                                        {isUpdatingMeta[candidate.id] && <Loader2 size={12} className="animate-spin text-slate-400" />}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Bottom Row: Minimal Icons */}
                                                <div className="flex items-center gap-3 text-[10px] text-slate-400 font-semibold mt-auto pt-1">
                                                    {candidate.matchScore ? (
                                                        <div className={`flex items-center gap-1 ${candidate.matchScore > 75 ? 'text-emerald-500' : 'text-amber-500'}`}>
                                                            <Star size={11} className="fill-current" /> {candidate.matchScore}%
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1 text-slate-300">
                                                            <Star size={11} /> -
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-1" title={`Toegevoegd: ${candidate.addedAt}`}>
                                                        <Clock size={11} /> {candidate.addedAt}
                                                    </div>
                                                    <div className="flex items-center gap-1" title="CV aanwezig">
                                                        {candidate.cvSummary ? <FileText size={11} className="text-slate-600" /> : <FileText size={11} className="text-slate-200" />}
                                                    </div>
                                                    {!isClientPortal &&
                                                        (stage.name === 'Voorgesteld' || stage.id === 'Voorgesteld') && (
                                                        <button
                                                            type="button"
                                                            title="Open voorstel venster (klantportaal / mail)"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMoveDetails(
                                                                    buildKanbanMoveDetails(candidate, stage.name, 'Voorgesteld', {
                                                                        vacancyId: vacancyId || '',
                                                                        clientId: clientId || null,
                                                                    }),
                                                                );
                                                                setShowMoveModal(true);
                                                            }}
                                                            className="ml-0.5 flex items-center gap-1 rounded-md border border-indigo-100 bg-indigo-50/80 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-indigo-800 transition hover:bg-indigo-100"
                                                        >
                                                            <MonitorSmartphone size={11} className="shrink-0" />
                                                            <span className="hidden min-[1180px]:inline">Voorstel</span>
                                                        </button>
                                                    )}

                                                    {/* Mobile Native Move Dropdown */}
                                                    {!isClientPortal && (
                                                    <div className="ml-auto flex items-center gap-1 lg:hidden" onClick={(e) => e.stopPropagation()}>
                                                        <select 
                                                            className="text-[9px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-1 appearance-none outline-none focus:ring-1 focus:ring-indigo-500 max-w-[100px] truncate"
                                                            value={stage.id}
                                                            onChange={(e) => handleMobileMove(candidate.id, e.target.value)}
                                                        >
                                                            {stages.map(s => (
                                                                <option key={s.id} value={s.id}>{s.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    )}

                                                    {/* Fast Actions */}
                                                    {!isClientPortal && (
                                                    <div className="ml-auto hidden lg:flex items-center gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setScheduleCandidate({
                                                                    applicationId: candidate.id,
                                                                    candidateId: candidate.candidateId,
                                                                    name: candidate.name,
                                                                    stageId: stage.id,
                                                                    initialDate: (candidate.pipelineMeta?.gesprek_date || '').slice(0, 10) || undefined,
                                                                    vacancyId: candidate.vacancyId,
                                                                });
                                                                setShowScheduler(true);
                                                            }}
                                                            className="text-purple-500/70 hover:text-purple-500 hover:scale-110 transition-all"
                                                            title="Plan interview (Microsoft Teams)"
                                                        >
                                                            <Calendar size={13} />
                                                        </button>
                                                        <button onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            const colIdx = columns.findIndex(c => c.candidates.some(cand => cand.id === candidate.id));
                                                            const nextStage = stages[colIdx + 1]?.name || 'Hired';
                                                            setMoveDetails(
                                                                buildKanbanMoveDetails(candidate, stage.name, nextStage, {
                                                                    vacancyId: vacancyId || '',
                                                                    clientId: clientId || null,
                                                                }),
                                                            );
                                                            setShowMoveModal(true);
                                                        }} className="text-emerald-500/70 hover:text-emerald-500 hover:scale-110 transition-all" title="Positief door naar volgende fase"><ThumbsUp size={13} /></button>
                                                        <button onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            setMoveDetails(
                                                                buildKanbanMoveDetails(candidate, stage.name, 'Rejected', {
                                                                    vacancyId: vacancyId || '',
                                                                    clientId: clientId || null,
                                                                }),
                                                            );
                                                            setShowMoveModal(true);
                                                        }} className="text-amber-500/70 hover:text-amber-500 hover:scale-110 transition-all font-bold group relative" title="Kandidaat afwijzen in deze fase"><span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] py-0.5 px-2 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Afwijzen (Blijft in pool)</span><ThumbsDown size={13} /></button>
                                                        <button onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            handleDeleteApplication(candidate.id);
                                                        }} className="text-red-500/70 hover:text-red-600 hover:scale-110 transition-all ml-1 group relative" title="Verwijder (Permanent uit pipeline)"><span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] py-0.5 px-2 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">Helemaal verwijderen</span><Ban size={13} /></button>
                                                    </div>
                                                    )}
                                                </div>

                                                {/* Disqualified Badge Overlay */}
                                                {candidate.isDeleted && filterType === 'Disqualified' && (
                                                    <div className="absolute top-3 right-3 flex items-center justify-center">
                                                        <span className="w-2 h-2 rounded-full bg-red-500" title="Verwijderd/Afgewezen"></span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    
                                    {/* Empty State Drop Zone Hint */}
                                    {visibleCandidates.length === 0 && (
                                        <div className={`h-24 border border-dashed rounded-[12px] flex items-center justify-center transition-colors ${isDragOver ? 'border-slate-400 bg-slate-200/50 text-slate-500' : 'border-slate-200 text-slate-300'}`}>
                                            <p className="text-[10px] font-bold uppercase tracking-widest">{isDragOver ? "Laat los om te verplaatsen" : "Leeg"}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* MOVE & COMMUNICATE MODAL */}
            <CandidateMoveModal
                isOpen={showMoveModal}
                onClose={() => setShowMoveModal(false)}
                moveDetails={moveDetails}
                templates={clientEmailTemplates}
                signature={clientEmailSignature}
                currentUser={currentUser}
                rejectionReasons={rejectionReasonsList}
                contextVacancySnippet={vacancySnippetForMoveModal}
                onConfirm={handleConfirmMove}
            />

            {/* TEAMS MEETING SCHEDULER MODAL */}
            {showScheduler && scheduleCandidate && vacancyId && (
                <TeamsMeetingScheduler
                    vacancyId={scheduleCandidate.vacancyId || vacancyId}
                    candidateId={scheduleCandidate.candidateId}
                    applicationId={scheduleCandidate.applicationId}
                    stageId={scheduleCandidate.stageId || activeStage}
                    initialDate={scheduleCandidate.initialDate}
                    clientId={clientId || undefined}
                    onClose={() => setShowScheduler(false)}
                    onScheduled={() => {
                        setShowScheduler(false);
                        if (vacancyId) {
                            void loadApplications(vacancyId, stages, true);
                        }
                    }}
                />
            )}



            {/* FEEDBACK MODAL */}
            {showFeedbackModal && feedbackSession && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                    <div className="w-full max-w-2xl animate-scaleIn relative">
                        <button
                            onClick={() => setShowFeedbackModal(false)}
                            className="absolute -top-12 right-0 text-white hover:text-slate-200 transition-colors"
                        >
                            <X size={24} />
                        </button>
                        <FeedbackScorecard
                            interviewId={feedbackSession.interviewId}
                            stageId={feedbackSession.stageId}
                            candidateName={feedbackSession.candidateName}
                            onSubmitted={() => {
                                setShowFeedbackModal(false);
                                alert("Feedback opgeslagen!");
                            }}
                        />
                    </div>
                </div>
            )}

            {/* ADD CANDIDATE MODAL */}
            {showAddModal && (
                <VacancyAddCandidateModal
                    vacancyId={vacancyId}
                    clientId={clientId}
                    onClose={() => setShowAddModal(false)}
                    onAdded={() => {
                        if (vacancyId === targetAddVacancyId || vacancyId === 'ALL_OPEN' || vacancyId?.startsWith('ALL_')) {
                            loadApplications(vacancyId);
                        }
                    }}
                />
            )}

            {/* INVITE REVIEWER MODAL */}
            {showInviteModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                    <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 animate-scaleIn">
                        <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-4">Nodig Reviewer Uit</h3>
                        <p className="text-xs text-slate-500 font-medium mb-4">Stuur een uitnodiging om kandidaten in deze vacature te beoordelen.</p>
                        <input
                            type="email"
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            placeholder="collega@bedrijf.nl"
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none mb-4"
                        />
                        <div className="flex gap-3">
                            <button onClick={() => setShowInviteModal(false)} className="flex-1 py-3 text-slate-500 font-bold text-xs uppercase hover:text-slate-800">Annuleren</button>
                            <button onClick={handleInviteReviewer} disabled={isInviting || !inviteEmail} className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black flex items-center justify-center gap-2">
                                {isInviting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Uitnodigen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REVIEW CANDIDATE MODAL */}
            {showReviewModal && reviewCandidate && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                    <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl p-8 animate-scaleIn">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Beoordeel Kandidaat</h3>
                                <p className="text-xs text-slate-500 font-bold">{reviewCandidate.name}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-black text-slate-500">
                                {reviewCandidate.name.charAt(0)}
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Score (1-10)</label>
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(s => (
                                        <button
                                            key={s}
                                            onClick={() => setReviewScore(s)}
                                            className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${reviewScore === s ? 'bg-amber-500 text-white scale-110 shadow-md' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Feedback</label>
                                <textarea
                                    value={reviewComment}
                                    onChange={e => setReviewComment(e.target.value)}
                                    placeholder="Wat vind je van deze kandidaat?"
                                    className="w-full h-32 p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none resize-none"
                                />
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => setShowReviewModal(false)} className="flex-1 py-3 text-slate-500 font-bold text-xs uppercase hover:text-slate-800">Annuleren</button>
                                <button onClick={submitReview} disabled={isSavingReview || reviewScore === 0} className="flex-1 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black flex items-center justify-center gap-2">
                                    {isSavingReview ? <Loader2 className="animate-spin" size={14} /> : <Star size={14} />} Opslaan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI MATCH MODAL */}
            {showAiModal && aiAnalysis && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                    <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl p-8 animate-scaleIn relative overflow-hidden">
                        <button onClick={() => setShowAiModal(false)} className="absolute top-6 right-6 p-2 bg-slate-50 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"><X size={18} /></button>

                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center text-purple-600 shadow-sm border border-purple-100">
                                <BrainCircuit size={32} />
                            </div>
                            <div>
                                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">AI Analyse</h2>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-2xl font-black ${aiAnalysis.score > 70 ? 'text-emerald-500' : 'text-amber-500'}`}>{aiAnalysis.score}%</span>
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Match Score</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Conclusie</h4>
                                <p className="text-sm font-medium text-slate-700 leading-relaxed italic">"{aiAnalysis.reason}"</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-3 flex items-center gap-2"><CheckCircle2 size={12} /> Pluspunten</h4>
                                    <ul className="space-y-2">
                                        {aiAnalysis.pros?.map((p: string, i: number) => (
                                            <li key={i} className="text-xs font-bold text-slate-600 bg-emerald-50/50 p-2 rounded-lg border border-emerald-100/50">{p}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-black text-red-500 uppercase tracking-widest mb-3 flex items-center gap-2"><Ban size={12} /> Aandachtspunten</h4>
                                    <ul className="space-y-2">
                                        {aiAnalysis.cons?.map((c: string, i: number) => (
                                            <li key={i} className="text-xs font-bold text-slate-600 bg-red-50/50 p-2 rounded-lg border border-red-100/50">{c}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showDetailModal && selectedCandidateId && clientId && (
                <CandidateDetailModal
                    candidateId={selectedCandidateId}
                    vacancyId={vacancyId}
                    clientId={clientId}
                    forceReadOnly={isClientPortal}
                    currentUser={
                        currentUser
                            ? {
                                  ...currentUser,
                                  name:
                                      currentUser.profile?.name ||
                                      currentUser.user_metadata?.full_name ||
                                      currentUser.name ||
                                      currentUser.email ||
                                      'Gebruiker',
                                  role: userRole,
                              }
                            : undefined
                    }
                    stages={stages}
                    onUpdate={() => loadApplications(vacancyId || 'ALL')}
                    onScheduleClick={() => {
                        const col = columns.find(c => c.candidates.some((x) => x.candidateId === selectedCandidateId));
                        const app = col?.candidates.find((a) => a.candidateId === selectedCandidateId);
                        if (app && col) {
                            setScheduleCandidate({
                                applicationId: app.id,
                                candidateId: app.candidateId,
                                name: app.name,
                                stageId: col.id,
                                vacancyId: app.vacancyId || vacancyId || undefined,
                                initialDate: (app.pipelineMeta?.gesprek_date || '').slice(0, 10) || undefined,
                            });
                            setShowScheduler(true);
                            setShowDetailModal(false);
                        }
                    }}
                    onClose={() => { setShowDetailModal(false); setSelectedCandidateId(null); }}
                />
            )}
            {/* Custom Toast Message */}
            {toastMessage && (
                <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-3 animate-in slide-in-from-bottom-5 z-[200]">
                    <CheckCircle2 className="text-emerald-400" size={20} />
                    <p className="text-sm font-bold">{toastMessage}</p>
                </div>
            )}
            {/* Bulk Modals */}
            {showBulkWhatsApp && (
                <BulkWhatsAppComposerModal
                    isOpen={showBulkWhatsApp}
                    onClose={() => setShowBulkWhatsApp(false)}
                    selectedCandidates={getSelectedCandidatesData()}
                    vacancyData={bulkComposerVacancyData}
                    onSuccessCallback={() => { setSelectedBulkCandidateIds([]); setIsBulkMode(false); window.location.reload(); }}
                />
            )}
            {showBulkEmail && (
                <BulkEmailComposerModal
                    isOpen={showBulkEmail}
                    onClose={() => setShowBulkEmail(false)}
                    selectedCandidates={getSelectedCandidatesData()}
                    vacancyData={bulkComposerVacancyData}
                    onSuccessCallback={() => { setSelectedBulkCandidateIds([]); setIsBulkMode(false); window.location.reload(); }}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteAppId && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-fadeIn">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-10 h-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                                <AlertTriangle size={20} />
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-slate-900 leading-tight">Kandidaat Verwijderen</h3>
                                <p className="text-sm text-slate-500 mt-1">Weet je zeker dat je deze kandidaat wilt verwijderen uit de vacature/pipeline? Dit kan niet ongedaan worden gemaakt.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setDeleteAppId(null)} className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Annuleren</button>
                            <button onClick={confirmDeleteApplication} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-bold transition-colors">Verwijderen</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KanbanBoard;




